import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { auth, db, storage } from "./firebase";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  deleteDoc,
} from "firebase/firestore";
import { ref as storageRef, getDownloadURL, getBlob } from "firebase/storage";
import { deleteObject } from "firebase/storage";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { applyEventTheme } from "./themeUtils";

export default function AdminDashboard() {
  const { eventId } = useParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [user, setUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [uploads, setUploads] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [totalUploads, setTotalUploads] = useState(0);
  const [zipProgress, setZipProgress] = useState({ current: 0, total: 0 });
  const [eventConfig, setEventConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);

  const [showPedro, setShowPedro] = useState(true);
  const [pedroStarted, setPedroStarted] = useState(false);
  const audioRef = useRef(null);
  const isRestrictedAdmin =
    user?.email?.toLowerCase().trim() === "viewphotos@eventsnap.pro";

  const handlePedroClick = () => {
    if (!pedroStarted) {
      audioRef.current?.play();
      setPedroStarted(true);
    } else {
      audioRef.current?.pause();
      setShowPedro(false);
    }
  };


  const observer = useRef();
  const PAGE_SIZE = 20;

  const loadingRef = useRef(loading);
  const lastDocRef = useRef(lastDoc);
  const hasMoreRef = useRef(hasMore);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    lastDocRef.current = lastDoc;
  }, [lastDoc]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  const selectedPhoto = selectedIndex !== null ? uploads[selectedIndex] : null;

  // 🔹 Fetch paginated uploads
  const fetchUploads = useCallback(async () => {
    if (!hasMoreRef.current || loadingRef.current) return;
    setLoading(true);

    try {
      const uploadsRef = collection(db, `weddings/${eventId}/uploads`);
      let q = query(uploadsRef, orderBy("timestamp", "desc"), limit(PAGE_SIZE));

      if (lastDocRef.current) {
        q = query(
          uploadsRef,
          orderBy("timestamp", "desc"),
          startAfter(lastDocRef.current),
          limit(PAGE_SIZE)
        );
      }

      const snap = await getDocs(q);

      if (!lastDocRef.current) {
        const totalSnap = await getDocs(uploadsRef);
        setTotalUploads(totalSnap.size);
      }

      const all = await Promise.all(
        snap.docs.map(async (docSnap) => {
          const data = docSnap.data();
          const path = `uploads/${eventId}/${data.uid}/${data.filename}`;
          const ref = storageRef(storage, path);
          let url = null;

          try {
            url = await getDownloadURL(ref);
          } catch (err) {
            if (err.code !== "storage/object-not-found") {
              console.warn(`⚠️ Failed to load preview for ${data.filename}`, err);
            }
          }

          return {
            ...data,
            url,
            fullUrl: url,
          };
        })
      );

      const validFiles = all.filter((file) => file.url);

      setUploads((prev) => [...prev, ...validFiles]);

      if (snap.docs.length < PAGE_SIZE) {
        setHasMore(false);
      } else {
        const nextDoc = snap.docs[snap.docs.length - 1];
        setLastDoc(nextDoc);
      }
    } catch (err) {
      console.error("Error loading uploads:", err);
      alert("Error loading uploads. Check permissions.");
    }

    setLoading(false);
  }, [eventId]);

  // 🔹 Observe last image for infinite scroll
  const lastImageRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          fetchUploads();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, fetchUploads]
  );

  // 🔹 Enforce proper admin auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      if (u) {
        if (u.isAnonymous) {
          await signOut(auth);
          setUser(null);
        } else {
          setUser(u);
        }
      } else {
        setUser(null);
      }
      setCheckingAuth(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setSelectedIndex(null);
    };
    if (selectedIndex !== null) {
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedIndex]);

  const fetchEventConfig = useCallback(async () => {
    try {
      const eventDoc = await getDoc(doc(db, "events", eventId));

      if (eventDoc.exists()) {
        const config = eventDoc.data();

        if (config.eventDate && typeof config.eventDate.toDate === 'function') {
          config.eventDate = config.eventDate.toDate();
        }

        setEventConfig(config);
        applyEventTheme(config);
      } else {
        console.error("❌ Event NOT found for AdminDashboard:", eventId);
        setEventConfig({
          coupleName: "Event Not Found",
          eventDate: new Date(),
          eventType: "Unknown Event",
          theme: "classic"
        });
        applyEventTheme({ theme: "classic" });
      }
    } catch (err) {
      console.error("❌ Error fetching event config for AdminDashboard:", err);
      setEventConfig({
        coupleName: "Unable to Load Event",
        eventDate: new Date(),
        eventType: "Error Loading",
        theme: "classic"
      });
      applyEventTheme({ theme: "classic" });
    }
    setLoadingConfig(false);
  }, [eventId]);

  useEffect(() => {
    fetchEventConfig();
  }, [fetchEventConfig]);

  const handleLogin = async () => {
    try {
      const res = await signInWithEmailAndPassword(auth, email, password);
      setUser(res.user);
    } catch (err) {
      alert("Login failed. Please check your credentials.");
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    setUser(null);
  };


  // 🔹 Download all as ZIP
  const handleDownloadAll = async () => {
    setDownloading(true);
    setZipProgress({ current: 0, total: 0 });

    const zip = new JSZip();
    const folder = zip.folder(`event-${eventId}`);
    const failedFiles = [];

    try {
      const uploadsRef = collection(db, `weddings/${eventId}/uploads`);
      const snap = await getDocs(uploadsRef);
      const allUploads = snap.docs.map((d) => d.data());
      const totalUploadsCount = allUploads.length;
      setZipProgress({ current: 0, total: totalUploadsCount });

      const CONCURRENCY = 5;
      const chunks = [];

      for (let i = 0; i < allUploads.length; i += CONCURRENCY) {
        chunks.push(allUploads.slice(i, i + CONCURRENCY));
      }

      const usedNames = new Set();

      for (let chunk of chunks) {
        await Promise.all(
          chunk.map(async (file) => {
            const path = `uploads/${eventId}/${file.uid}/${file.filename}`;
            const ref = storageRef(storage, path);
            try {
              const blob = await tryGetBlobWithRetry(ref, 2);

              // Unique filename
              let name = file.filename;
              let counter = 1;
              while (usedNames.has(name)) {
                const ext = name.includes(".") ? name.substring(name.lastIndexOf(".")) : "";
                const base = name.replace(ext, "");
                name = `${base} (${counter})${ext}`;
                counter++;
              }
              usedNames.add(name);

              folder.file(name, blob);
            } catch (err) {
              console.warn(`❌ Failed to include ${file.filename}`, err);
              failedFiles.push(file.filename);
            } finally {
              setZipProgress((prev) => ({
                current: Math.min(prev.current + 1, totalUploadsCount),
                total: totalUploadsCount,
              }));
            }
          })
        );
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const safeName = (eventConfig?.coupleName || eventId).replace(/[^a-zA-Z0-9À-žа-яА-Я\s\-_]/g, "").trim().replace(/\s+/g, "-");
      saveAs(zipBlob, `${safeName}-photos.zip`);

      if (failedFiles.length > 0) {
        alert(`⚠️ Some files were not included:\n${failedFiles.join("\n")}`);
      }
    } catch (err) {
      console.error("ZIP generation error:", err);
      alert("Failed to download all files.");
    }

    setDownloading(false);
    setZipProgress({ current: 0, total: 0 });
  };


  const tryGetBlobWithRetry = async (ref, retries = 2) => {
    let lastError;
    for (let i = 0; i <= retries; i++) {
      try {
        return await getBlob(ref);
      } catch (err) {
        lastError = err;
        await new Promise((res) => setTimeout(res, 500));
      }
    }
    throw lastError;
  };



  useEffect(() => {
    if (user) {
      const checkAuthorization = async () => {
        const eventDoc = await getDoc(doc(db, `events/${eventId}`));
        if (eventDoc.exists()) {
          const data = eventDoc.data();
          if (!(data.admins || []).includes(user.email)) {
            alert("Not authorized for this event.");
            await signOut(auth);
            setUser(null);
          } else {
            await fetchUploads();
          }
        } else {
          alert("Event not found.");
          await signOut(auth);
          setUser(null);
        }
      };
      checkAuthorization();
    }
  }, [user, eventId, fetchUploads]);

  // 🔹 Download single image
  const downloadCurrentPhoto = async () => {
    if (!selectedPhoto) return;
    const path = `uploads/${eventId}/${selectedPhoto.uid}/${selectedPhoto.filename}`;
    const ref = storageRef(storage, path);
    const blob = await getBlob(ref);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = selectedPhoto.filename;
    a.click();
  };

  // 🔹 Delete single image
  const deleteCurrentPhoto = async () => {
    if (isRestrictedAdmin) return;
    if (!selectedPhoto) return;
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedPhoto.filename}?`
    );
    if (!confirmDelete) return;

    try {
      // Delete from Firebase Storage
      const path = `uploads/${eventId}/${selectedPhoto.uid}/${selectedPhoto.filename}`;
      const ref = storageRef(storage, path);
      await deleteObject(ref);

      // Delete from Firestore
      const uploadsRef = collection(db, `weddings/${eventId}/uploads`);
      const q = query(
        uploadsRef,
        where("uid", "==", selectedPhoto.uid),
        where("filename", "==", selectedPhoto.filename)
      );
      const snap = await getDocs(q);
      for (let docSnap of snap.docs) {
        await deleteDoc(doc(db, `weddings/${eventId}/uploads/${docSnap.id}`));
      }

      // Refresh local state
      setUploads((prev) =>
        prev.filter((f) => f.filename !== selectedPhoto.filename)
      );
      setTotalUploads((prev) => prev - 1);
      setSelectedIndex(null);
      alert("✅ Photo deleted.");
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete photo.");
    }
  };

  const formatDate = (date) => {
    if (!date) return "";
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('ro-RO', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const formatThumbDate = (timestamp) => {
    if (!timestamp) return "";
    const d = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    return d.toLocaleDateString('ro-RO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  if (checkingAuth) return <p className="p-4 admin-status">Checking authentication...</p>;

  if (loadingConfig) return <p className="p-4 admin-status">Loading event theme...</p>;

  if (!user) {
    return (
      <div className="container py-5 admin-dashboard admin-login-view">
        <h2 className="mb-4 admin-title">Admin Login</h2>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="form-control mb-3 admin-input"
          placeholder="Email"
        />
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="form-control mb-3 admin-input"
          placeholder="Password"
        />
        <button onClick={handleLogin} className="btn admin-btn-primary w-100">
          Login
        </button>
      </div>
    );
  }

  const isVideo = (filename) => /\.(mp4|mov|avi|mkv|quicktime)$/i.test(filename);

  return (
    <div className="admin-dashboard">

      {/* Pedro Easter Egg */}
      {showPedro && (
        <div className="pedro-overlay" onClick={handlePedroClick}>
          <audio ref={audioRef} src="/pedro.mp3?v=2" loop />
          <div className={`pedro-glow-ring${pedroStarted ? " pedro-spinning" : ""}`}>
            <div className="pedro-circle">
              <img src="/pedro-raccoon.jpg" alt="Pedro" className="pedro-raccoon" />
            </div>
          </div>
          {!pedroStarted ? (
            <>
              <p className="pedro-text">
                {"Pedro Pedro Pedro...".split("").map((ch, i) => (
                  <span key={i} style={{ animationDelay: `${i * 0.06}s` }}>{ch}</span>
                ))}
              </p>
              <div className="pedro-start-btn">▶ Click pentru Pedro</div>
            </>
          ) : (
            <>
              <p className="pedro-text">
                {"Pedro Pedro Pedro...".split("").map((ch, i) => (
                  <span key={i} style={{ animationDelay: `${i * 0.06}s` }}>{ch}</span>
                ))}
              </p>
              <p className="pedro-skip">Click oriunde pentru a închide</p>
            </>
          )}
        </div>
      )}

      {/* Event Header */}
      <div className="admin-event-header">
        <div className="admin-event-header-inner">
          <div className="admin-event-info">
            <h1 className="admin-event-name">{eventConfig?.coupleName || "Admin Dashboard"}</h1>
            <p className="admin-event-meta">
              {eventConfig?.eventType || "Event"} · {formatDate(eventConfig?.eventDate)}
            </p>
          </div>
          <div className="d-flex align-items-center gap-3">
            <div className="admin-stat-pill">
              <span className="admin-stat-number">{totalUploads}</span>
              <span className="admin-stat-label">fișiere</span>
            </div>
            {uploads.length > 0 && (
              <button
                onClick={handleDownloadAll}
                disabled={downloading}
                className="btn admin-btn-primary"
              >
                {downloading ? "Se pregătește..." : "⬇ Download ZIP"}
              </button>
            )}
            <button onClick={handleLogout} className="btn admin-btn-danger">
              Logout
            </button>
          </div>
        </div>

        {downloading && zipProgress.total > 0 && (
          <div className="admin-zip-progress">
            <p className="admin-zip-label">
              Se pregătesc {zipProgress.current} din {zipProgress.total} fișiere
              ({Math.round((zipProgress.current / zipProgress.total) * 100)}%)...
            </p>
            <div className="progress" style={{ height: "6px" }}>
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                style={{ width: `${(zipProgress.current / zipProgress.total) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Gallery Grid */}
      <div className="admin-gallery-grid">
        <div className="row g-2 g-md-3">
          {uploads.map((file, i) => (
            <div
              key={i}
              className="col-6 col-sm-4 col-md-3 col-lg-2"
              ref={i === uploads.length - 1 ? lastImageRef : null}
            >
              <div className="admin-thumb" onClick={() => setSelectedIndex(i)}>
                {isVideo(file.filename) ? (
                  <div className="admin-thumb-media-wrap">
                    <video className="admin-thumb-media">
                      <source src={file.url} />
                    </video>
                    <div className="admin-play-overlay">
                      <div className="admin-play-icon">▶</div>
                    </div>
                  </div>
                ) : (
                  <div className="admin-thumb-media-wrap">
                    <img
                      src={file.url}
                      alt={`Upload ${i + 1}`}
                      className="admin-thumb-media"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="admin-thumb-footer">
                  <span className="admin-thumb-date">{formatThumbDate(file.timestamp)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {loading && (
          <div className="text-center py-4">
            <div className="spinner-border spinner-border-sm" role="status" style={{ color: "var(--gold)" }} />
            <p className="mt-2 small" style={{ color: "var(--muted)" }}>Se încarcă...</p>
          </div>
        )}

        {!loading && uploads.length === 0 && (
          <div className="admin-empty-state">
            <div className="admin-empty-state-card">
              <div className="admin-empty-state-icon">📷</div>
              <h3 className="admin-empty-state-title">No photos yet</h3>
              <p className="admin-empty-state-text">
                As soon as guests upload their memories, they will appear here in this gallery.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {selectedPhoto && (
        <div
          className="admin-lightbox"
          onClick={() => setSelectedIndex(null)}
        >
          <div
            className="admin-lightbox-inner"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Toolbar */}
            <div className="admin-lightbox-toolbar">
              <span className="admin-lightbox-counter">{selectedIndex + 1} / {uploads.length}</span>
              <div className="d-flex gap-2">
                <button className="btn admin-btn-primary admin-lb-btn" onClick={downloadCurrentPhoto}>
                  ⬇ Download
                </button>
                {!isRestrictedAdmin && (
                  <button className="btn admin-btn-danger admin-lb-btn" onClick={deleteCurrentPhoto}>
                    🗑 Delete
                  </button>
                )}
                <button className="btn admin-btn-neutral admin-lb-btn" onClick={() => setSelectedIndex(null)}>
                  ✕
                </button>
              </div>
            </div>

            {/* Left arrow */}
            <button
              className="admin-lightbox-arrow admin-lightbox-arrow-left"
              onClick={() => setSelectedIndex((selectedIndex - 1 + uploads.length) % uploads.length)}
            >
              ‹
            </button>

            {/* Media */}
            {isVideo(selectedPhoto.filename) ? (
              <video
                controls
                autoPlay
                className="admin-lightbox-media"
              >
                <source src={selectedPhoto.fullUrl || selectedPhoto.url} />
              </video>
            ) : (
              <img
                src={selectedPhoto.fullUrl || selectedPhoto.url}
                alt={`Upload ${selectedIndex + 1}`}
                className="admin-lightbox-media"
              />
            )}

            {/* Right arrow */}
            <button
              className="admin-lightbox-arrow admin-lightbox-arrow-right"
              onClick={() => setSelectedIndex((selectedIndex + 1) % uploads.length)}
            >
              ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
}